// Phase 2 — Zustand store for the new video draft preview-then-commit flow.
//
// Holds the current draft state, polls the backend every 5s while the
// director is running, and provides actions for create / approve / reject.
//
// Persistence: while a draft is in flight (status = directing) we save the
// reference_id to localStorage so the user can refresh the page and resume.
//
// This store is INDEPENDENT of the legacy videoGenerationStore. The
// Step2VideoStudio component decides which one to use based on a feature flag.

import { create } from "zustand";
import toast from "react-hot-toast";
import {
  approveVideoDraft,
  createVideoDraft,
  editVideoDraftScript,
  getVideoDraft,
  regenerateVideoDraftImage,
  regenerateVideoDraftStyle,
  rejectVideoDraft,
} from "@/services/ads/videos/videoDraftService";
import {
  VIDEO_DRAFT_STATUS,
  type CreateVideoDraftRequest,
  type VideoDraftResponse,
} from "@/services/ads/videos/videoDraft.types";

const STORAGE_KEY = "fluxi_video_draft_in_flight";

const POLL_INTERVAL_MS = 5_000;
// Director should respond in ~30s. We give it 5 minutes max before timing out.
const MAX_POLL_ATTEMPTS_DIRECTOR = 60;
// Post-approve: imagen base ~25s + Kling render ~5min × 2 + merge ~30s.
// Total ~11 min realistic, 15 min worst case with FAL retries.
const MAX_POLL_ATTEMPTS_RENDER = 180;

// Phase 5.2: nuevos estados post-approve para que la pantalla muestre la
// imagen base apenas existe y luego transicione naturalmente al video final
// sin volver al gallery legacy.
type DraftStoreStatus =
  | "idle"
  | "directing" // director Gemini corriendo
  | "ready" // preview listo, esperando approve/reject
  | "approving" // approve en flight
  | "rejecting" // reject en flight
  | "editing_script" // Phase 5.4 — edit-script en flight (sin polling)
  | "regenerating_image" // Phase 5.4 — regenerate-image en flight (sin polling)
  | "regenerating_style" // Phase 5.4 — regenerate-style en flight + polling hasta que vuelva a ready
  | "rendering" // post-approve: generating_image | waiting_scenes | merging
  | "finished" // video final listo, polling apagado
  | "done" // estado terminal limpio (post-reject)
  | "error";

interface VideoDraftState {
  /** Internal state of the store (not the same as draft.status). */
  storeStatus: DraftStoreStatus;
  /** The current draft response from the backend. */
  draft: VideoDraftResponse | null;
  /** True while a network call is in flight. */
  isLoading: boolean;
  /** Error message to surface in the UI. */
  errorMessage: string | null;

  // Actions
  startDraft: (payload: CreateVideoDraftRequest) => Promise<boolean>;
  approve: () => Promise<boolean>;
  reject: () => Promise<boolean>;
  /** Phase 5.4: edita el script inline. No regenera imagen ni cuenta como regen. */
  editScript: (scriptPartA: string, scriptPartB?: string | null) => Promise<boolean>;
  /**
   * Phase 5.4: regenera la imagen base. Cuenta como 1 regen.
   * Phase 6 v2: para drafts UGC se puede pasar `target` para regenerar
   * solo una imagen específica (portrait, scene_a, scene_b) en lugar
   * de todas. Kling drafts ignoran el target. Default: "all".
   */
  regenerateImage: (
    instruction?: string | null,
    target?: "portrait" | "scene_a" | "scene_b" | "all" | null
  ) => Promise<boolean>;
  /**
   * Phase 5.4: cambia el creative pattern. Cuenta como 1 regen, vuelve a
   * directing. Opcionalmente se le puede pasar una `instruction` en
   * lenguaje natural ("más enojado", "menos dramático") que el director
   * usa como hint para ajustar el tono. El backend la pasa como
   * user_instruction al LLM.
   */
  regenerateStyle: (patternKey?: string | null, instruction?: string | null) => Promise<boolean>;
  reset: () => void;
  /** Internal — resumes polling after a page refresh if there's an in-flight draft. */
  resumeFromStorage: () => void;
}

let pollingHandle: ReturnType<typeof setInterval> | null = null;
let pollingAttempts = 0;

function stopPolling() {
  if (pollingHandle) {
    clearInterval(pollingHandle);
    pollingHandle = null;
  }
  pollingAttempts = 0;
}

function persistInFlight(referenceId: string) {
  try {
    localStorage.setItem(STORAGE_KEY, referenceId);
  } catch {
    // ignore — localStorage may be unavailable
  }
}

function clearInFlight() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

function loadInFlight(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export const useVideoDraftStore = create<VideoDraftState>((set, get) => {
  const handlePolledResponse = (resp: VideoDraftResponse) => {
    set({ draft: resp });

    switch (resp.status) {
      case VIDEO_DRAFT_STATUS.READY_FOR_REVIEW:
        // If approved_at is already set we already moved past review. The
        // backend uses ready_for_review as the "preview ready" state, but
        // approve transitions to generating_image so we don't normally re-enter
        // here after approve. Defensive though.
        if (resp.approved_at) {
          // already approved, treat as rendering
          return;
        }
        stopPolling();
        // Do NOT clearInFlight() here — keep the reference_id in localStorage
        // so the user can reload the page and resume the preview. Only clear
        // on terminal states (ERROR, REJECTED, FINISHED).
        set({ storeStatus: "ready" });
        return;

      case VIDEO_DRAFT_STATUS.ERROR:
        stopPolling();
        clearInFlight();
        set({
          storeStatus: "error",
          errorMessage:
            resp.error_message ||
            "Algo falló durante la creación. Tus créditos fueron devueltos.",
        });
        return;

      case VIDEO_DRAFT_STATUS.REJECTED:
        stopPolling();
        clearInFlight();
        set({ storeStatus: "done" });
        return;

      case VIDEO_DRAFT_STATUS.FINISHED:
        // Final video ready. Stop polling and let the UI render the player.
        stopPolling();
        clearInFlight();
        set({ storeStatus: "finished" });
        return;

      case VIDEO_DRAFT_STATUS.GENERATING_IMAGE:
      case VIDEO_DRAFT_STATUS.WAITING_SCENES:
      case VIDEO_DRAFT_STATUS.MERGING:
        // Post-approve render in progress. Stay in rendering state and keep
        // polling so the UI picks up base_image_url and eventually video_url.
        set({ storeStatus: "rendering" });
        return;

      case VIDEO_DRAFT_STATUS.DIRECTING:
      case VIDEO_DRAFT_STATUS.MEDIA_GENERATING:
        // keep polling, director still working
        return;

      default:
        // Unexpected state, stop polling and surface as error.
        stopPolling();
        clearInFlight();
        set({
          storeStatus: "error",
          errorMessage: `Estado desconocido del preview: ${resp.status}`,
        });
    }
  };

  const startPolling = (referenceId: string, maxAttempts: number) => {
    stopPolling();
    pollingAttempts = 0;

    pollingHandle = setInterval(async () => {
      pollingAttempts += 1;
      if (pollingAttempts > maxAttempts) {
        stopPolling();
        clearInFlight();
        set({
          storeStatus: "error",
          errorMessage:
            "Está tardando más de lo esperado. Si algo falló, tus créditos serán devueltos automáticamente.",
        });
        return;
      }

      const { res } = await getVideoDraft(referenceId);
      if (res) handlePolledResponse(res);
      // If error, just continue polling — could be a transient network blip.
    }, POLL_INTERVAL_MS);
  };

  return {
    storeStatus: "idle",
    draft: null,
    isLoading: false,
    errorMessage: null,

    async startDraft(payload) {
      set({ isLoading: true, errorMessage: null, draft: null, storeStatus: "directing" });

      const { res, error } = await createVideoDraft(payload);

      set({ isLoading: false });

      if (error || !res) {
        set({ storeStatus: "error" });
        return false;
      }

      set({ draft: res });
      persistInFlight(res.reference_id);

      // If the server already returned ready_for_review (synchronous fast path),
      // skip polling entirely. Otherwise start polling.
      if (res.status === VIDEO_DRAFT_STATUS.READY_FOR_REVIEW) {
        set({ storeStatus: "ready" });
        clearInFlight();
      } else {
        startPolling(res.reference_id, MAX_POLL_ATTEMPTS_DIRECTOR);
      }

      return true;
    },

    async approve() {
      const draft = get().draft;
      if (!draft) return false;

      set({ isLoading: true, storeStatus: "approving", errorMessage: null });
      const { res, error } = await approveVideoDraft(draft.reference_id);
      set({ isLoading: false });

      if (error || !res) {
        set({ storeStatus: "ready" });
        return false;
      }

      // Phase 5.2: instead of closing the modal after approve, transition into
      // rendering and resume polling so the UI can show the base image as soon
      // as it's generated and then the final video player. The render typically
      // takes 5-7 minutes total — we cap at 12 minutes worst case.
      set({ draft: res, storeStatus: "rendering" });
      persistInFlight(draft.reference_id);
      startPolling(draft.reference_id, MAX_POLL_ATTEMPTS_RENDER);
      return true;
    },

    async reject() {
      const draft = get().draft;
      if (!draft) return false;

      set({ isLoading: true, storeStatus: "rejecting", errorMessage: null });
      const { res, error } = await rejectVideoDraft(draft.reference_id);
      set({ isLoading: false });

      if (error || !res) {
        set({ storeStatus: "ready" });
        return false;
      }

      set({ draft: res, storeStatus: "done" });
      toast.success("Cancelado. Te devolvimos los créditos.");
      return true;
    },

    async editScript(scriptPartA, scriptPartB) {
      const draft = get().draft;
      if (!draft) return false;

      set({ isLoading: true, storeStatus: "editing_script", errorMessage: null });
      const { res, error } = await editVideoDraftScript(draft.reference_id, {
        script_part_a: scriptPartA,
        script_part_b: scriptPartB ?? null,
      });
      set({ isLoading: false });

      if (error || !res) {
        set({ storeStatus: "ready" });
        return false;
      }

      set({ draft: res, storeStatus: "ready" });
      toast.success("Guion actualizado.");
      return true;
    },

    async regenerateImage(instruction, target) {
      const draft = get().draft;
      if (!draft) return false;

      set({ isLoading: true, storeStatus: "regenerating_image", errorMessage: null });
      const { res, error } = await regenerateVideoDraftImage(draft.reference_id, {
        instruction: instruction ?? null,
        target: target ?? null,
      });
      set({ isLoading: false });

      if (error || !res) {
        set({ storeStatus: "ready" });
        return false;
      }

      set({ draft: res, storeStatus: "ready" });
      toast.success("Imagen regenerada.");
      return true;
    },

    async regenerateStyle(patternKey, instruction) {
      const draft = get().draft;
      if (!draft) return false;

      set({ isLoading: true, storeStatus: "regenerating_style", errorMessage: null });
      const { res, error } = await regenerateVideoDraftStyle(draft.reference_id, {
        pattern_key: patternKey ?? null,
        instruction: instruction ?? null,
      });
      set({ isLoading: false });

      if (error || !res) {
        set({ storeStatus: "ready" });
        return false;
      }

      // El backend devolvió el draft transicionado a directing. Resume polling
      // hasta que vuelva a ready_for_review (con el nuevo payload e imagen).
      set({ draft: res, storeStatus: "directing" });
      persistInFlight(draft.reference_id);
      startPolling(draft.reference_id, MAX_POLL_ATTEMPTS_DIRECTOR);
      return true;
    },

    reset() {
      stopPolling();
      clearInFlight();
      set({
        storeStatus: "idle",
        draft: null,
        isLoading: false,
        errorMessage: null,
      });
    },

    resumeFromStorage() {
      const referenceId = loadInFlight();
      if (!referenceId) return;
      // Try to recover the draft. If the backend says it's still directing,
      // resume polling. If it's already ready/error/rejected, jump straight
      // to that state.
      set({ storeStatus: "directing" });
      getVideoDraft(referenceId).then(({ res }) => {
        if (res) {
          handlePolledResponse(res);
          if (
            res.status === VIDEO_DRAFT_STATUS.DIRECTING ||
            res.status === VIDEO_DRAFT_STATUS.MEDIA_GENERATING
          ) {
            startPolling(referenceId, MAX_POLL_ATTEMPTS_DIRECTOR);
          } else if (
            res.status === VIDEO_DRAFT_STATUS.GENERATING_IMAGE ||
            res.status === VIDEO_DRAFT_STATUS.WAITING_SCENES ||
            res.status === VIDEO_DRAFT_STATUS.MERGING
          ) {
            startPolling(referenceId, MAX_POLL_ATTEMPTS_RENDER);
          }
        } else {
          // Backend doesn't know about it anymore; clear local state.
          clearInFlight();
          set({ storeStatus: "idle" });
        }
      });
    },
  };
});
